import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Input,
} from "@monbits/ui";
import * as React from "react";
import { useForm } from "react-hook-form";
import type { Merchant, MerchantBase } from "src/types";
import * as z from "zod";
import LinkInput, { validateUrl } from "../link-input/link-input";

interface Props {
	activeMerchant?: Merchant;
	onFinish: (values: MerchantBase, reset: () => void) => Promise<void>;
	isFormSubmitting: boolean;
}

const linkPrefix = "https://";

const formSchema = z.object({
	name: z.string().min(1, "Field is required"),
	website: z
		.string()
		.min(1, "Field is required")
		.refine((val) => validateUrl(val, true), {
			message: "Incorrect url value",
		}),
});

type FormValues = z.infer<typeof formSchema>;

const MerchantForm: React.FC<Props> = (props: Props) => {
	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: "",
			website: "",
		},
	});

	React.useEffect(() => {
		if (props.activeMerchant) {
			const websiteWithoutProtocol = props.activeMerchant.website.startsWith(
				"https://",
			)
				? props.activeMerchant.website.slice(8)
				: props.activeMerchant.website;

			form.reset({
				name: props.activeMerchant.name,
				website: websiteWithoutProtocol,
			});
		}
	}, [props.activeMerchant, form]);

	const onSubmit = async (values: FormValues) => {
		const submissionValues: MerchantBase = {
			name: values.name,
			website: linkPrefix + values.website,
		};
		await props.onFinish(submissionValues, form.reset);
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Store name</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="website"
					render={({ field, fieldState }) => (
						<LinkInput
							label="Website"
							name="website"
							placeholder="your-store.com"
							required
							value={field.value}
							onChange={field.onChange}
							error={fieldState.error?.message}
						/>
					)}
				/>
				<Button type="submit" className="sr-only">
					Submit
				</Button>
			</form>
		</Form>
	);
};

export default MerchantForm;
